import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { setActiveBrandId } from '../lib/active-brand.js';
import { createBrandApi, PlanLimitError, scrapeUrlIntoBrief } from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { refreshQueries } from '../lib/refresh.js';
import { deriveBrandNameFromUrl, isValidSiteUrl } from '../lib/site-url.js';
import { Mag } from './mag.js';
import { magToast } from './toaster.js';

// Shared "start a new brand" form. Used in two surfaces:
//  - /brands inline (the workspace-level brand list)
//  - CreateBrandModal (triggered from the top-nav brand-switcher)
//
// URL-first: the founder pastes their marketing site (not a name). We
// derive a starter brand name from the domain, create the brand, kick
// off the site read, and drop them straight on the live scrape page —
// folding the old separate "name it" + "paste your site" steps into one.
// Mag's extracted company name surfaces in the brief for review/rename;
// we intentionally do NOT auto-rename the brand from it.
//
// Owns the input + the API calls + the handoff into onboarding. The
// caller's `onCreated` callback runs after the brand is created (before
// navigation) so it can collapse a panel / close a modal / refresh a
// list on the now-stale surface before mag's read takes over the
// viewport.

export interface CreateBrandFormProps {
  /** Called after the brand is created and active, before the
   *  navigation into onboarding. Caller uses this to collapse a panel
   *  (brands page), close a modal (top-nav), or refresh a list. */
  onCreated?: (brand: { id: string; slug: string }) => void | Promise<void>;
  /** Optional className applied to the form root. Defaults to an
   *  unstyled form; callers wrap with their own card chrome. */
  className?: string;
}

export function CreateBrandForm({ onCreated, className }: CreateBrandFormProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const urlIsValid = isValidSiteUrl(url);

  const handleCreate = async () => {
    if (!urlIsValid) {
      magToast.error('drop in your site.', "i'll read it and name the brand from what i find.");
      return;
    }
    // Starter name from the domain ("commitmind.dev" → "commitmind").
    // Always resolves for a valid url (host has a dot), but guard anyway.
    const name = deriveBrandNameFromUrl(url) ?? 'new brand';
    setSubmitting(true);
    try {
      const brand = await createBrandApi({ name });
      setActiveBrandId(brand.id);
      setUrl('');
      // Refetch brands so the new row is in cache before any onboarding
      // route's beforeLoad reads it by slug (else it bounces to /brands).
      await queryClient.invalidateQueries({ queryKey: ['brands'] });
      await onCreated?.({ id: brand.id, slug: brand.slug });

      try {
        // Kick off the read. scrapeUrlIntoBrief stamps brand.marketingUrl
        // synchronously + returns a runId; the orchestrator runs in the
        // background and the live scrape page polls it. This is what lets
        // us SKIP the standalone /onboarding/url step.
        const result = await scrapeUrlIntoBrief(url, brand.id);
        // refreshQueries (refetch + AWAIT), NOT invalidateQueries: the
        // /onboarding/scrape route reads brand.marketingUrl out of the
        // brands cache in its beforeLoad to render the browser-window
        // address bar. scrapeUrlIntoBrief just stamped marketingUrl
        // server-side; we must await the refetch so the page shows the
        // real site instead of the https://<slug> fallback
        // ("https://commitmind" for the commitmind.dev brand).
        await refreshQueries(queryClient, ['brands', 'brand-brain']);
        magToast.success(`${brand.name} is in.`, 'reading your site now.');
        navigate({
          to: '/brand/$slug/onboarding/scrape',
          params: { slug: brand.slug },
          search: { runId: result.runId },
        });
      } catch (scrapeErr) {
        // The brand exists but the read couldn't be started (network /
        // auth). Fall back to the standalone url step so they can retry
        // — it re-stamps the url + re-kicks the orchestrator.
        magToast.error(
          "mag couldn't start reading that site.",
          scrapeErr instanceof Error ? scrapeErr.message : 'try again on the next screen.',
        );
        navigate({ to: '/brand/$slug/onboarding/url', params: { slug: brand.slug } });
      }
    } catch (err) {
      // s119 — plan ceiling. Surface the structured message from the api
      // (e.g. "Founder Pack covers 1 brand. Upgrade to Growth…").
      if (err instanceof PlanLimitError) {
        magToast.error("you've hit your brand limit.", err.payload.message);
      } else {
        magToast.error("couldn't create the brand.", founderErrorCopy(err));
      }
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleCreate();
      }}
      className={className}
    >
      <div className="flex items-start gap-3">
        <Mag size={36} mood="casting" />
        <div className="flex-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>new brand</Trans>
          </p>
          <p className="mt-1.5 font-serif text-[18px] leading-tight text-[var(--ink)]">
            <Trans>
              “paste your site — i'll read it, name the brand, and pull a brief together.”
            </Trans>
          </p>
          <div className="mt-3 flex gap-2">
            {/* Static https:// adornment — the founder types just the
                host. The onChange strips a pasted scheme so the field
                never shows "https://https://…"; the validator prepends
                https:// before parsing, so either way works. */}
            <div className="relative flex-1">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-[12px] top-1/2 -translate-y-1/2 select-none font-mono text-[13px] text-[var(--ink)]/45"
              >
                <Trans>https://</Trans>
              </span>
              <input
                type="text"
                inputMode="url"
                autoComplete="url"
                value={url}
                onChange={(e) => setUrl(e.target.value.replace(/^https?:\/\//i, ''))}
                placeholder={t`yourbrand.com`}
                disabled={submitting}
                maxLength={2048}
                // biome-ignore lint/a11y/noAutofocus: opens via a click (modal trigger or "+ new brand" toggle) so focus follows intent — typing should be the very next action
                autoFocus
                style={{ paddingLeft: '78px' }}
                className="w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-[14px] outline-none focus:border-[var(--ink)]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !urlIsValid}
              className={urlIsValid || submitting ? 'btn disabled:opacity-60' : 'btn btn-ghost'}
            >
              {submitting ? <Trans>reading…</Trans> : <Trans>let mag read it</Trans>}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
